import {
  generateOtp,
  resyncAccount,
  resyncAccounts,
} from "./generated/admin/admin"
import { updateMarketingBanner } from "./generated/alerts/alerts"
import {
  cancelAccountRegistration,
  createAccountCredit,
  editAccountReviewPeriod,
  openJuniferUi,
  postSmartPayAsYouGo,
  submitRefundApprovalRequest,
  updatePaperBill,
} from "./generated/account/account"
import {
  createAccountContact,
  updateAccountContact,
} from "./generated/account-contact/account-contact"
import { createAccountNote } from "./generated/account-notes/account-notes"
import { updateAccountTicket } from "./generated/account-tickets/account-tickets"
import {
  createAccountUnderReviewPeriod,
  cancelAccountUnderReviewPeriod,
} from "./generated/account-under-review-periods/account-under-review-periods"
import { storeVulnerabilitiesAndAssociatedServices } from "./generated/psr/psr"
import { changeOfTenancy } from "./generated/change-of-tenancy/change-of-tenancy"
import { getComplaintsSummary } from "./generated/complaint/complaint"
import { updateDcaAllocationConfig } from "./generated/dca-allocation-config/dca-allocation-config"
import {
  createThrottleConfig,
  discardThrottleEvent,
  resumeThrottleEvent,
  updateThrottleConfig,
} from "./generated/email-throttle/email-throttle"
import { retryEnrolment } from "./generated/enrolment/enrolment"
import { updateFeatureFlag } from "./generated/feature-flags/feature-flags"
import { updatePaymentSchedule } from "./generated/payment-schedule-updater/payment-schedule-updater"
import { createDirectDebit } from "./generated/direct-debit/direct-debit"
import { verifyPaymentMethod } from "./generated/payment-methods/payment-methods"
import { createPaymentSession } from "./generated/payments/payments"
import {
  createPaymentPlan,
  cancelPaymentPlan,
} from "./generated/payment-plans/payment-plans"
import {
  allocatePrizes,
  closePrizeDraw,
  createDraw,
  createPrizeDrawReward,
  deletePrizeDrawReward,
  reprocessDrawWinners,
  updateDraw,
  updatePrizeDrawReward,
  updateWinner,
  winnersNotified,
} from "./generated/loyalty/loyalty"
import { createRenewal } from "./generated/renewals/renewals"
import { applyReferralCode, createUserFromJunifer } from "./generated/user/user"
import {
  createSmsBatch,
  validateSmsBatch,
} from "./generated/sms-batch/sms-batch"
import {
  getSmartMetersDataFileUrl,
  removeSmartMeterInterest,
  saveSmartMeterInterest,
  uploadSmartMetersEligibleForExchange,
} from "./generated/smart-meters/smart-meters"
import { uploadInstallCodes } from "./generated/install-codes/install-codes"
import {
  cancelOutboundRequest,
  createOutboundPhotoRequest,
  sendFollowUpEmailForOutboundRequest,
  submitMeterReading,
} from "./generated/meter-reading/meter-reading"
import { estimateMeterRead } from "./generated/meter-read-estimator/meter-read-estimator"
import {
  bulkXRITM,
  meterpointXRITM,
  updateMeterPointConsents,
} from "./generated/meterpoints/meterpoints"
import {
  deletePaymentReviewRecordByIds,
  getPaymentReviewResultsFileContent,
  retryPaymentReviewRecordsByFilters,
  retryPaymentReviewRecordsById,
} from "./generated/payment-reviews/payment-reviews"
import {
  addToMeterExchangeJourney,
  deleteByAccountNumber,
  removeFromMeterExchangeJourney,
} from "./generated/warrants/warrants"
import {
  AccountListResyncRequest,
  ApplyReferralCodeRequest,
  BulkXRITMRequest,
  CancelAccountRequest,
  CancelPaymentPlanParams,
  CancelPaymentPlanRequest,
  CardPaymentRequestDto,
  ChangeOfTenancyRequestDto,
  ComplaintFilters,
  CreateAccountContactRequest,
  CreateAccountCreditRequest,
  CreateAccountNoteRequest,
  CreateAccountUnderReviewPeriodRequest,
  CreateDirectDebitRequest,
  CreateDrawRequest,
  CreatePaymentPlanRequest,
  CreatePaymentScheduleRequest,
  CreateRefundApprovalRequest,
  CreateRenewalRequest,
  CreateThrottleConfigBody,
  CreateUserFromJuniferRequest,
  EstimateMeterReadRequest,
  DeletePaymentReviewRecordsRequest,
  DownloadPaymentReviewsRequest,
  FileAsAStream,
  DrawRewardRequest,
  EditAccountReviewPeriodRequest,
  JuniferAccountNumber,
  OutboundPhotoRequest,
  OutboundRequestStatus,
  PaymentMethodVerificationRequest,
  PaymentReviewRetryRequest,
  PaymentReviewsRetryRequest,
  PsrCustomerSettings,
  InstallCodesBulkUploadRequest,
  ResyncRequest,
  SmartMeterInterestRequest,
  SmartMetersDashboardDownloadRequest,
  SmartMetersEligibleForExchangeBulkUploadRequest,
  SmartPayAsYouGoRequest,
  SubmitMeterReadingRequest,
  UpdateMeterPointConsentsRequest,
  SmsBatchCreateRequest,
  UpdateAccountContact,
  UpdateAccountTicketRequest,
  UpdateDcaAllocationConfigParams,
  UpdateDcaAllocationConfigRequest,
  UpdateDrawRequest,
  UpdateFeatureFlagBody,
  UpdateMarketingBannerRequest,
  UpdatePaperBill,
  UpdatePrizeDrawEntryWinnerRequest,
  UpdateThrottleConfigBody,
  WarrantsAddToMeterExchange,
  WarrantsDeleteAccount,
  WarrantsRemoveFromMeterExchange,
} from "./generated/model"

export type UpdateThrottleConfigPayload = UpdateThrottleConfigBody & {
  id: number
}

export interface CancelAccountRegistrationPayload {
  billingAccountId: number
  body: CancelAccountRequest
}

export interface CreateAccountContactPayload {
  billingAccountId: number
  body: CreateAccountContactRequest
}

export interface UpdateAccountContactPayload {
  billingAccountId: number
  juniferContactId: number
  body: UpdateAccountContact
}

export interface CreateAccountNotePayload {
  billingAccountId: number
  body: CreateAccountNoteRequest
}

export interface UpdateAccountTicketPayload {
  billingAccountId: number
  juniferTicketId: number
  body: UpdateAccountTicketRequest
}

export interface CreateReviewPeriodPayload {
  billingAccountId: number
  body: CreateAccountUnderReviewPeriodRequest
}

export interface CancelReviewPeriodPayload {
  billingAccountId: number
  reviewPeriodId: number
}

export interface EditReviewPeriodPayload {
  billingAccountId: number
  body: EditAccountReviewPeriodRequest
}

export interface UpdatePaperBillPayload {
  billingAccountId: number
  body: UpdatePaperBill
}

export interface StorePsrPayload {
  customerId: number
  body: PsrCustomerSettings
}

export interface UpdatePaymentSchedulePayload {
  billingAccountId: number
  body: CreatePaymentScheduleRequest
}

export interface CreateDirectDebitPayload {
  accountId: number
  body: CreateDirectDebitRequest
}

export interface CreateRenewalPayload {
  billingAccountId: number
  input: CreateRenewalRequest
}

export interface CreatePaymentPlanPayload {
  accountId: number
  body: CreatePaymentPlanRequest
}

export interface CancelPaymentPlanPayload {
  accountId: number
  body: CancelPaymentPlanRequest
  params?: CancelPaymentPlanParams
}

export interface RequestRefundPayload {
  billingAccountId: number
  body: CreateRefundApprovalRequest
}

export type DrawIdPayload = { drawId: number }
export type UpdateDrawPayload = { drawId: number; body: UpdateDrawRequest }
export type CreateRewardPayload = { drawId: number; body: DrawRewardRequest }
export type UpdateRewardPayload = {
  drawId: number
  rewardId: number
  body: DrawRewardRequest
}
export type DeleteRewardPayload = { drawId: number; rewardId: number }
export type UpdateWinnerPayload = {
  drawId: number
  winnerId: number
  body: UpdatePrizeDrawEntryWinnerRequest
}

export interface SaveSmartMeterInterestPayload {
  billingAccountId: string
  body: SmartMeterInterestRequest
}

export interface SubmitMeterReadingPayload {
  billingAccountId: number
  body: SubmitMeterReadingRequest
}

export interface CreateOutboundPhotoRequestPayload {
  accountNumber: string
  body: OutboundPhotoRequest
}

export interface SendOutboundFollowUpPayload {
  outboundRequestId: number
  currentStatus: OutboundRequestStatus
}

export interface UpdateMeterpointConsentsPayload {
  accountId: number
  body: UpdateMeterPointConsentsRequest
}

export interface SubmitSmartPayAsYouGoPayload {
  billingAccountNumber: JuniferAccountNumber
  input: SmartPayAsYouGoRequest
}

export interface RetryPaymentReviewsByFiltersPayload {
  paymentReviewId: number
  body: PaymentReviewRetryRequest
}

export interface ApplyReferralCodePayload {
  customerId: number
  body: ApplyReferralCodeRequest
}

export type UpdateMarketingBannerPayload = {
  id: number
  body: UpdateMarketingBannerRequest
}

export const BackofficeRestMutations = {
  account: {
    openJuniferUi: () => ({
      mutationFn: (billingAccountId: number) => openJuniferUi(billingAccountId),
    }),
    requestRefund: () => ({
      mutationFn: ({ billingAccountId, body }: RequestRefundPayload) =>
        submitRefundApprovalRequest(billingAccountId, body),
    }),
    cancelRegistration: () => ({
      mutationFn: ({
        billingAccountId,
        body,
      }: CancelAccountRegistrationPayload) =>
        cancelAccountRegistration(billingAccountId, body),
    }),
    createCredit: () => ({
      mutationFn: (body: CreateAccountCreditRequest) =>
        createAccountCredit(body),
    }),
    createContact: () => ({
      mutationFn: ({ billingAccountId, body }: CreateAccountContactPayload) =>
        createAccountContact(billingAccountId, body),
    }),
    updateContact: () => ({
      mutationFn: ({
        billingAccountId,
        juniferContactId,
        body,
      }: UpdateAccountContactPayload) =>
        updateAccountContact(billingAccountId, juniferContactId, body),
    }),
    createNote: () => ({
      mutationFn: ({ billingAccountId, body }: CreateAccountNotePayload) =>
        createAccountNote(billingAccountId, body),
    }),
    updateTicket: () => ({
      mutationFn: ({
        billingAccountId,
        juniferTicketId,
        body,
      }: UpdateAccountTicketPayload) =>
        updateAccountTicket(billingAccountId, juniferTicketId, body),
    }),
  },
  user: {
    createFromJunifer: () => ({
      mutationFn: (body: CreateUserFromJuniferRequest) =>
        createUserFromJunifer(body),
    }),
    applyReferralCode: () => ({
      mutationFn: ({ customerId, body }: ApplyReferralCodePayload) =>
        applyReferralCode(customerId, body),
    }),
  },
  smartMeter: {
    download: () => ({
      mutationFn: (body: SmartMetersDashboardDownloadRequest) =>
        getSmartMetersDataFileUrl(body),
    }),
    saveSmartMeterInterest: () => ({
      mutationFn: ({ billingAccountId, body }: SaveSmartMeterInterestPayload) =>
        saveSmartMeterInterest(billingAccountId, body),
    }),
    uploadSmartMetersEligibleForExchange: () => ({
      mutationFn: (body: SmartMetersEligibleForExchangeBulkUploadRequest) =>
        uploadSmartMetersEligibleForExchange(body),
    }),
    uploadInstallCodes: () => ({
      mutationFn: (body: InstallCodesBulkUploadRequest) =>
        uploadInstallCodes(body),
    }),
    removeSmartMeterInterest: () => ({
      mutationFn: (billingAccountId: string) =>
        removeSmartMeterInterest(billingAccountId),
    }),
  },
  readings: {
    submit: () => ({
      mutationFn: ({ billingAccountId, body }: SubmitMeterReadingPayload) =>
        submitMeterReading(billingAccountId, body),
    }),
    estimate: () => ({
      mutationFn: (body: EstimateMeterReadRequest) => estimateMeterRead(body),
    }),
    createOutboundPhotoRequest: () => ({
      mutationFn: ({
        accountNumber,
        body,
      }: CreateOutboundPhotoRequestPayload) =>
        createOutboundPhotoRequest(body, { accountNumber }),
    }),
    cancelOutboundRequest: () => ({
      mutationFn: (outboundRequestId: number) =>
        cancelOutboundRequest(outboundRequestId),
    }),
    sendFollowUpEmailForOutboundRequest: () => ({
      mutationFn: ({
        outboundRequestId,
        currentStatus,
      }: SendOutboundFollowUpPayload) =>
        sendFollowUpEmailForOutboundRequest(outboundRequestId, {
          currentStatus,
        }),
    }),
  },
  meterpoints: {
    updateConsent: () => ({
      mutationFn: ({ accountId, body }: UpdateMeterpointConsentsPayload) =>
        updateMeterPointConsents(accountId, body),
    }),
    fetchSmartRead: (meterpointIdentifier: string) => ({
      mutationFn: () => meterpointXRITM({ meterpointIdentifier }),
    }),
    bulkSmartReads: () => ({
      mutationFn: (body: BulkXRITMRequest) => bulkXRITM(body),
    }),
  },
  smartPayAsYouGo: {
    submit: () => ({
      mutationFn: ({
        billingAccountNumber,
        input,
      }: SubmitSmartPayAsYouGoPayload) =>
        postSmartPayAsYouGo(input, { billingAccountNumber }),
    }),
  },
  paymentReviews: {
    retryRecordsByIds: () => ({
      mutationFn: (body: PaymentReviewsRetryRequest) =>
        retryPaymentReviewRecordsById(body),
    }),
    retryRecordsByFilters: () => ({
      mutationFn: ({
        paymentReviewId,
        body,
      }: RetryPaymentReviewsByFiltersPayload) =>
        retryPaymentReviewRecordsByFilters(paymentReviewId, body),
    }),
    deleteRecordsByIds: () => ({
      mutationFn: (body: DeletePaymentReviewRecordsRequest) =>
        deletePaymentReviewRecordByIds(body),
    }),
    downloadRecords: () => ({
      mutationFn: (body: DownloadPaymentReviewsRequest) =>
        getPaymentReviewResultsFileContent(body),
    }),
  },
  smsBatch: {
    validate: () => ({
      mutationFn: (body: FileAsAStream) => validateSmsBatch(body),
    }),
    create: () => ({
      mutationFn: (body: SmsBatchCreateRequest) => createSmsBatch(body),
    }),
  },
  reviewPeriods: {
    create: () => ({
      mutationFn: ({ billingAccountId, body }: CreateReviewPeriodPayload) =>
        createAccountUnderReviewPeriod(billingAccountId, body),
    }),
    cancel: () => ({
      mutationFn: ({
        billingAccountId,
        reviewPeriodId,
      }: CancelReviewPeriodPayload) =>
        cancelAccountUnderReviewPeriod(billingAccountId, reviewPeriodId),
    }),
    edit: () => ({
      mutationFn: ({ billingAccountId, body }: EditReviewPeriodPayload) =>
        editAccountReviewPeriod(billingAccountId, body),
    }),
  },
  paperBill: {
    update: () => ({
      mutationFn: ({ billingAccountId, body }: UpdatePaperBillPayload) =>
        updatePaperBill(billingAccountId, body),
    }),
  },
  psr: {
    store: () => ({
      mutationFn: ({ customerId, body }: StorePsrPayload) =>
        storeVulnerabilitiesAndAssociatedServices(customerId, body),
    }),
  },
  admin: {
    generateOtp: () => ({
      mutationFn: ({ userId }: { userId: number }) =>
        generateOtp({ id: userId }),
    }),
    resyncAccount: () => ({
      mutationFn: (body: ResyncRequest) => resyncAccount(body),
    }),
    resyncAccounts: () => ({
      mutationFn: (body: AccountListResyncRequest) => resyncAccounts(body),
    }),
  },
  throttle: {
    createConfig: () => ({
      mutationFn: (body: CreateThrottleConfigBody) =>
        createThrottleConfig(body),
    }),
    updateConfig: () => ({
      mutationFn: ({ id, ...body }: UpdateThrottleConfigPayload) =>
        updateThrottleConfig(id, body),
    }),
    resumeEvent: () => ({
      mutationFn: (id: number) => resumeThrottleEvent(id),
    }),
    discardEvent: () => ({
      mutationFn: (id: number) => discardThrottleEvent(id),
    }),
  },
  enrolment: {
    retry: () => ({
      mutationFn: (enrolmentId: number) => retryEnrolment(enrolmentId),
    }),
  },
  renewal: {
    create: () => ({
      mutationFn: ({ billingAccountId, input }: CreateRenewalPayload) =>
        createRenewal(billingAccountId, input),
    }),
  },
  warrants: {
    deleteByAccountNumber: () => ({
      mutationFn: (body: WarrantsDeleteAccount) => deleteByAccountNumber(body),
    }),
    addToMeterExchange: () => ({
      mutationFn: (variables: {
        billingAccountId: number
        body: WarrantsAddToMeterExchange
      }) =>
        addToMeterExchangeJourney(variables.billingAccountId, variables.body),
    }),
    removeFromMeterExchange: () => ({
      mutationFn: (variables: {
        billingAccountId: number
        body: WarrantsRemoveFromMeterExchange
      }) =>
        removeFromMeterExchangeJourney(
          variables.billingAccountId,
          variables.body,
        ),
    }),
  },
  dcaAllocation: {
    updateConfig: () => ({
      mutationFn: ({
        params,
        body,
      }: {
        params: UpdateDcaAllocationConfigParams
        body: UpdateDcaAllocationConfigRequest
      }) => updateDcaAllocationConfig(body, params),
    }),
  },
  featureFlags: {
    update: () => ({
      mutationFn: ({ id, body }: { id: number; body: UpdateFeatureFlagBody }) =>
        updateFeatureFlag(id, body),
    }),
  },
  payments: {
    updatePaymentSchedule: () => ({
      mutationFn: ({ billingAccountId, body }: UpdatePaymentSchedulePayload) =>
        updatePaymentSchedule(billingAccountId, body),
    }),
    createDirectDebit: () => ({
      mutationFn: ({ accountId, body }: CreateDirectDebitPayload) =>
        createDirectDebit(accountId, body),
    }),
    verifyPaymentMethod: () => ({
      mutationFn: (body: PaymentMethodVerificationRequest) =>
        verifyPaymentMethod(body),
    }),
    createPaymentSession: () => ({
      mutationFn: (body: CardPaymentRequestDto) => createPaymentSession(body),
    }),
  },
  paymentPlans: {
    create: () => ({
      mutationFn: ({ accountId, body }: CreatePaymentPlanPayload) =>
        createPaymentPlan(accountId, body),
    }),
    cancel: () => ({
      mutationFn: ({ accountId, body, params }: CancelPaymentPlanPayload) =>
        cancelPaymentPlan(accountId, body, params),
    }),
  },
  complaint: {
    getDynamicSummary: () => ({
      mutationFn: (body: ComplaintFilters) => getComplaintsSummary(body),
    }),
  },
  tenancy: {
    create: () => ({
      mutationFn: (body: ChangeOfTenancyRequestDto) => changeOfTenancy(body),
    }),
  },
  alertsBanners: {
    updateMarketingBanner: () => ({
      mutationFn: ({ id, body }: UpdateMarketingBannerPayload) =>
        updateMarketingBanner(id, body),
    }),
  },
  loyalty: {
    createDraw: () => ({
      mutationFn: (body: CreateDrawRequest) => createDraw(body),
    }),
    updateDraw: () => ({
      mutationFn: (payload: UpdateDrawPayload) =>
        updateDraw(payload.drawId, payload.body),
    }),
    createReward: () => ({
      mutationFn: (payload: CreateRewardPayload) =>
        createPrizeDrawReward(payload.drawId, payload.body),
    }),
    updateReward: () => ({
      mutationFn: (payload: UpdateRewardPayload) =>
        updatePrizeDrawReward(payload.drawId, payload.rewardId, payload.body),
    }),
    deleteReward: () => ({
      mutationFn: (payload: DeleteRewardPayload) =>
        deletePrizeDrawReward(payload.drawId, payload.rewardId),
    }),
    updateWinner: () => ({
      mutationFn: (payload: UpdateWinnerPayload) =>
        updateWinner(payload.drawId, payload.winnerId, payload.body),
    }),
    closeDraw: () => ({
      mutationFn: (payload: DrawIdPayload) => closePrizeDraw(payload.drawId),
    }),
    reprocessWinners: () => ({
      mutationFn: (payload: DrawIdPayload) =>
        reprocessDrawWinners(payload.drawId),
    }),
    notifyWinners: () => ({
      mutationFn: (payload: DrawIdPayload) => winnersNotified(payload.drawId),
    }),
    grantPrizes: () => ({
      mutationFn: (payload: DrawIdPayload) => allocatePrizes(payload.drawId),
    }),
  },
}
